import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash2, FileText, BellRing, Undo2, Laptop, Smartphone, Tablet, KeyRound, CreditCard, Wrench, HardHat, Package } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/company-property`;
const ICONS = { laptop: Laptop, phone: Smartphone, tablet: Tablet, key_fob: KeyRound, keys: KeyRound, fuel_card: CreditCard, tools: Wrench, ppe: HardHat, other: Package };
const STATUS = {
  awaiting_signature: 'bg-amber-100 text-amber-800 border-amber-300',
  issued: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  returned: 'bg-slate-100 text-slate-600 border-slate-300',
};
const fmt = (s) => (s ? new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '');
const INPUT = 'w-full px-2 py-1.5 border border-slate-300 rounded text-sm text-slate-900 bg-white outline-none focus:border-blue-500';

function IssueForm({ userId, meta, prefill, onDone, onCancel }) {
  const blank = () => ({ type: 'laptop', description: '', identifier: '', photo_required: true });
  const [rows, setRows] = useState(() => (prefill && prefill.length)
    ? prefill.map(r => ({ type: r.type || 'other', description: r.description || '', identifier: '', photo_required: !!(meta.item_types.find(t => t.value === r.type)?.photo) }))
    : [blank()]);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const setRow = (i, k, v) => setRows(rs => rs.map((r, j) => {
    if (j !== i) return r;
    const n = { ...r, [k]: v };
    if (k === 'type') n.photo_required = !!(meta.item_types.find(t => t.value === v)?.photo);
    return n;
  }));
  const submit = async () => {
    const items = rows.filter(r => r.description.trim());
    if (!items.length) { toast.error('Describe at least one item'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/issue`, { employee_id: userId, items, note });
      toast.success('Items issued — employee has been notified to sign');
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not issue items'); }
    setSaving(false);
  };
  return (
    <div className="border border-blue-200 bg-blue-50/50 rounded-lg p-3 space-y-2" data-testid="property-issue-form">
      <p className="text-xs text-slate-600">Add each item being handed over. The employee gets a push notification and must confirm each item is in good working order and sign. Laptops, tablets and phones need a photo of the serial / IMEI.</p>
      {rows.map((r, i) => (
        <div key={i} className="grid grid-cols-12 gap-2 items-center">
          <select value={r.type} onChange={e => setRow(i, 'type', e.target.value)} data-testid={`prop-row-type-${i}`} className={`${INPUT} col-span-4 sm:col-span-3`}>
            {meta.item_types.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
          <input value={r.description} onChange={e => setRow(i, 'description', e.target.value)} placeholder="Description (e.g. Dell Latitude 5540, grey)" data-testid={`prop-row-desc-${i}`} className={`${INPUT} col-span-8 sm:col-span-4`} />
          <input value={r.identifier} onChange={e => setRow(i, 'identifier', e.target.value)} placeholder="Serial / asset tag / fob #" data-testid={`prop-row-id-${i}`} className={`${INPUT} col-span-7 sm:col-span-3`} />
          <label className="col-span-4 sm:col-span-1 flex items-center gap-1 text-[11px] text-slate-600 whitespace-nowrap">
            <input type="checkbox" checked={r.photo_required} onChange={e => setRow(i, 'photo_required', e.target.checked)} data-testid={`prop-row-photo-${i}`} /> photo
          </label>
          <button onClick={() => setRows(rs => rs.length > 1 ? rs.filter((_, j) => j !== i) : rs)} className="col-span-1 text-slate-500 hover:text-red-600 justify-self-end"><Trash2 className="w-4 h-4" /></button>
        </div>
      ))}
      <div className="flex flex-wrap items-center gap-2">
        <button onClick={() => setRows(rs => [...rs, blank()])} data-testid="prop-add-row" className="inline-flex items-center gap-1 px-2 py-1 text-xs text-blue-700 bg-blue-100 hover:bg-blue-200 rounded"><Plus className="w-3.5 h-3.5" /> Another item</button>
        <input value={note} onChange={e => setNote(e.target.value)} placeholder="Note to employee (optional)" className={`${INPUT} flex-1 min-w-[180px]`} />
        <button onClick={onCancel} className="px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 rounded">Cancel</button>
        <button onClick={submit} disabled={saving} data-testid="prop-issue-submit" className="px-4 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm font-medium disabled:opacity-50">{saving ? 'Issuing…' : 'Issue & request signature'}</button>
      </div>
    </div>
  );
}

function RejectPhotoDialog({ item, onDone, onCancel }) {
  const [reason, setReason] = useState('');
  const send = async () => {
    try { await axios.post(`${API}/items/${item.id}/reject-photo`, { reason }); toast.success('Sent back — employee asked to retake the photo'); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2 bg-red-50 border border-red-200 rounded p-2 mt-1" data-testid={`prop-reject-form-${item.id}`}>
      <span className="text-xs text-red-700 font-medium">Why retake?</span>
      <input value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. serial number is blurry / cut off" data-testid={`prop-reject-reason-${item.id}`} className={`${INPUT} flex-1 min-w-[180px]`} />
      <button onClick={onCancel} className="px-2 py-1 text-xs text-slate-500">Cancel</button>
      <button onClick={send} data-testid={`prop-reject-send-${item.id}`} className="px-3 py-1 bg-red-600 text-white rounded text-xs">Ask to retake</button>
    </div>
  );
}

function ReturnDialog({ item, onDone, onCancel }) {
  const [cond, setCond] = useState('good');
  const [note, setNote] = useState('');
  const save = async () => {
    try { await axios.post(`${API}/items/${item.id}/return`, { condition: cond, note }); toast.success('Marked returned'); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2 bg-slate-50 border border-slate-200 rounded p-2 mt-1" data-testid={`prop-return-form-${item.id}`}>
      <span className="text-xs text-slate-600">Returned in:</span>
      {['good', 'damaged', 'lost'].map(c => (
        <label key={c} className="text-xs flex items-center gap-1 capitalize"><input type="radio" checked={cond === c} onChange={() => setCond(c)} data-testid={`prop-return-${c}-${item.id}`} /> {c}</label>
      ))}
      <input value={note} onChange={e => setNote(e.target.value)} placeholder="Note" className={`${INPUT} flex-1 min-w-[140px]`} />
      <button onClick={onCancel} className="px-2 py-1 text-xs text-slate-500">Cancel</button>
      <button onClick={save} data-testid={`prop-return-save-${item.id}`} className="px-3 py-1 bg-slate-100 text-slate-900 rounded text-xs">Save</button>
    </div>
  );
}

function NotifySettings({ meta, onSaved }) {
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState(() => new Set((meta.notify_users || []).map(u => u.id)));
  const [q, setQ] = useState('');
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try { const r = await axios.put(`${API}/settings`, { notify_user_ids: [...sel] }); toast.success('Notification list saved'); onSaved(r.data); setOpen(false); }
    catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };
  const staff = (meta.staff || []).filter(u => !q || u.full_name.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="text-xs" data-testid="prop-notify-settings">
      <div className="flex flex-wrap items-center gap-1 text-slate-500">
        <BellRing className="w-3.5 h-3.5" /> When signed, notify the issuer{(meta.notify_users || []).length ? ' + ' : ''}
        {(meta.notify_users || []).map(u => <span key={u.id} className="px-1.5 py-0.5 bg-slate-100 rounded text-slate-700" data-testid={`prop-notify-chip-${u.id}`}>{u.full_name}</span>)}
        <button onClick={() => setOpen(o => !o)} data-testid="prop-notify-edit" className="ml-1 text-blue-600 hover:underline">{open ? 'close' : 'edit'}</button>
      </div>
      {open && (
        <div className="mt-2 border border-slate-200 rounded-lg p-2 bg-white">
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search staff…" data-testid="prop-notify-search" className={INPUT} />
          <div className="max-h-40 overflow-y-auto mt-2 grid grid-cols-1 sm:grid-cols-2 gap-1">
            {staff.map(u => (
              <label key={u.id} className="flex items-center gap-2 px-1 py-0.5 rounded hover:bg-slate-50 cursor-pointer">
                <input type="checkbox" checked={sel.has(u.id)} data-testid={`prop-notify-opt-${u.id}`}
                  onChange={e => setSel(prev => { const n = new Set(prev); e.target.checked ? n.add(u.id) : n.delete(u.id); return n; })} />
                <span className="text-slate-800">{u.full_name}</span><span className="text-slate-500">{u.role}</span>
              </label>
            ))}
          </div>
          <div className="flex justify-end gap-2 mt-2">
            <button onClick={() => setOpen(false)} className="px-2 py-1 text-slate-500">Cancel</button>
            <button onClick={save} disabled={saving} data-testid="prop-notify-save" className="px-3 py-1 bg-blue-600 text-white rounded disabled:opacity-50">Save</button>
          </div>
          <p className="text-[10px] text-slate-500 mt-1">They get a push notification, an in-app bell alert and an email with the Drive link every time any employee signs.</p>
        </div>
      )}
    </div>
  );
}

export default function PropertyIssuePanel({ userId, prefill = [], onPrefillConsumed }) {
  const [data, setData] = useState(null);
  const [meta, setMeta] = useState(null);
  const [issuing, setIssuing] = useState(false);
  const [returning, setReturning] = useState(null);
  const [rejecting, setRejecting] = useState(null);
  const token = localStorage.getItem('token');
  const acceptPhoto = async (it) => { try { await axios.post(`${API}/items/${it.id}/accept-photo`); toast.success('Photo accepted'); load(); } catch { toast.error('Failed'); } };
  const load = useCallback(async () => {
    try { const r = await axios.get(`${API}/employee/${userId}`); setData(r.data); } catch { setData({ items: [], agreements: [] }); }
  }, [userId]);
  useEffect(() => { load(); axios.get(`${API}/meta`).then(r => setMeta(r.data)).catch(() => {}); }, [load]);
  useEffect(() => { if (prefill.length && meta) setIssuing(true); }, [prefill, meta]);

  const cancel = async (ag) => {
    if (!window.confirm('Cancel this unsigned agreement and remove its items?')) return;
    try { await axios.delete(`${API}/agreements/${ag.id}`); toast.success('Cancelled'); load(); } catch { toast.error('Failed'); }
  };
  const remind = async (ag) => {
    try { const r = await axios.post(`${API}/agreements/${ag.id}/remind`); toast.success(r.data.sent ? 'Reminder pushed' : 'No push device registered — ask them to enable notifications'); load(); }
    catch { toast.error('Failed'); }
  };
  const unreturn = async (it) => { try { await axios.post(`${API}/items/${it.id}/unreturn`); load(); } catch { toast.error('Failed'); } };

  if (!data || !meta) return <p className="text-xs text-slate-500">Loading company property…</p>;
  const agById = Object.fromEntries((data.agreements || []).map(a => [a.id, a]));
  const active = data.items.filter(i => i.status !== 'returned');
  const returned = data.items.filter(i => i.status === 'returned');

  const Row = ({ it }) => {
    const Icon = ICONS[it.type] || Package;
    const ag = agById[it.agreement_id];
    return (
      <div className="border border-slate-200 rounded-lg p-2" data-testid={`prop-item-${it.id}`}>
        <div className="flex items-start gap-2">
          <Icon className="w-4 h-4 text-slate-500 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-sm text-slate-800 font-medium">{it.type_label}: {it.description}{it.identifier && <span className="text-slate-500 font-normal"> · {it.identifier}</span>}</div>
            <div className="text-[11px] text-slate-500">
              Issued {fmt(it.issued_at)} by {it.issued_by}
              {it.status === 'issued' && it.signed_at ? ` · signed ${fmt(it.signed_at)}` : ''}
              {it.status === 'returned' ? ` · returned ${fmt(it.returned_at)} (${it.return_condition}${it.return_note ? ` — ${it.return_note}` : ''}) by ${it.returned_by}` : ''}
            </div>
          </div>
          <span className={`px-2 py-0.5 rounded-full border text-[10px] font-semibold whitespace-nowrap ${STATUS[it.status]}`} data-testid={`prop-item-status-${it.id}`}>
            {it.status === 'awaiting_signature' ? 'Awaiting signature' : it.status === 'issued' ? 'Signed & issued' : 'Returned'}
          </span>
        </div>
        <div className="flex flex-wrap gap-1 mt-1 pl-6">
          {it.status === 'issued' && ag?.status === 'signed' && (
            <a href={`${API}/agreements/${ag.id}/pdf?token=${encodeURIComponent(token)}`} target="_blank" rel="noreferrer" data-testid={`prop-pdf-${it.id}`}
              className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] bg-slate-100 hover:bg-slate-200 rounded"><FileText className="w-3 h-3" /> Signed PDF</a>
          )}
          {it.photo_id && <a href={`${API}/photos/${it.photo_id}?token=${encodeURIComponent(token)}`} target="_blank" rel="noreferrer" data-testid={`prop-photo-link-${it.id}`} className="px-2 py-0.5 text-[11px] bg-slate-100 hover:bg-slate-200 rounded">Serial photo</a>}
          {it.status === 'issued' && it.photo_status === 'rejected' && <span className="px-2 py-0.5 text-[11px] bg-red-100 text-red-700 rounded" data-testid={`prop-photo-status-${it.id}`} title={it.photo_reject_reason}>Retake requested · waiting on employee</span>}
          {it.status === 'issued' && it.photo_status === 'replaced' && <span className="px-2 py-0.5 text-[11px] bg-amber-100 text-amber-800 rounded" data-testid={`prop-photo-status-${it.id}`}>New photo — please review</span>}
          {it.status === 'issued' && it.photo_status === 'accepted' && <span className="px-2 py-0.5 text-[11px] bg-emerald-100 text-emerald-700 rounded" data-testid={`prop-photo-status-${it.id}`}>Photo accepted</span>}
          {it.status === 'issued' && it.photo_id && it.photo_status !== 'rejected' && rejecting !== it.id && (
            <>
              {it.photo_status !== 'accepted' && <button onClick={() => acceptPhoto(it)} data-testid={`prop-accept-photo-${it.id}`} className="px-2 py-0.5 text-[11px] bg-emerald-50 text-emerald-700 hover:bg-emerald-100 rounded">Accept photo</button>}
              <button onClick={() => setRejecting(it.id)} data-testid={`prop-reject-photo-${it.id}`} className="px-2 py-0.5 text-[11px] bg-red-50 text-red-700 hover:bg-red-100 rounded">Reject photo</button>
            </>
          )}
          {it.status === 'issued' && returning !== it.id && <button onClick={() => setReturning(it.id)} data-testid={`prop-return-btn-${it.id}`} className="px-2 py-0.5 text-[11px] bg-slate-100 text-slate-900 rounded hover:bg-slate-50">Mark returned</button>}
          {it.status === 'returned' && <button onClick={() => unreturn(it)} className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] text-slate-500 hover:text-slate-800"><Undo2 className="w-3 h-3" /> undo</button>}
          {it.status === 'awaiting_signature' && ag && (
            <>
              <button onClick={() => remind(ag)} data-testid={`prop-remind-${ag.id}`} className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] bg-amber-100 text-amber-800 hover:bg-amber-200 rounded"><BellRing className="w-3 h-3" /> Remind{ag.reminders_sent ? ` (${ag.reminders_sent})` : ''}</button>
              <button onClick={() => cancel(ag)} data-testid={`prop-cancel-${ag.id}`} className="px-2 py-0.5 text-[11px] text-red-600 hover:bg-red-50 rounded">Cancel agreement</button>
            </>
          )}
        </div>
        {returning === it.id && <ReturnDialog item={it} onDone={() => { setReturning(null); load(); }} onCancel={() => setReturning(null)} />}
        {rejecting === it.id && <RejectPhotoDialog item={it} onDone={() => { setRejecting(null); load(); }} onCancel={() => setRejecting(null)} />}
      </div>
    );
  };

  return (
    <div className="space-y-2" data-testid="property-issue-panel">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-slate-700">Issued Company Property <span className="text-slate-500 font-normal">({active.length} out)</span></p>
        {!issuing && <button onClick={() => setIssuing(true)} data-testid="prop-issue-btn" className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-medium"><Plus className="w-3.5 h-3.5" /> Issue items</button>}
      </div>
      {issuing && <IssueForm key={prefill.map(r => r.description).join('|')} userId={userId} meta={meta} prefill={prefill}
        onDone={() => { setIssuing(false); onPrefillConsumed?.(); load(); }} onCancel={() => { setIssuing(false); onPrefillConsumed?.(); }} />}
      {meta.can_manage && <NotifySettings meta={meta} onSaved={setMeta} />}
      {active.length === 0 && !issuing && <p className="text-xs text-slate-500" data-testid="prop-empty">Nothing issued yet — laptops, phones, key fobs and anything else handed out will be tracked here with a signed receipt.</p>}
      {active.map(it => <Row key={it.id} it={it} />)}
      {returned.length > 0 && (
        <details className="text-xs">
          <summary className="cursor-pointer text-slate-500">Returned items ({returned.length})</summary>
          <div className="space-y-1 mt-1">{returned.map(it => <Row key={it.id} it={it} />)}</div>
        </details>
      )}
    </div>
  );
}
